import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Plus, 
  ThumbsUp, 
  ThumbsDown, 
  Lightbulb, 
  Zap, 
  Bug, 
  Clock, 
  CheckCircle2, 
  XCircle,
  TrendingUp,
  MessageSquarePlus,
  Shield,
  Trash2,
  Settings
} from "lucide-react";

const categoryIcons = {
  feature: <Lightbulb className="w-4 h-4" />,
  improvement: <Zap className="w-4 h-4" />,
  bug: <Bug className="w-4 h-4" />
};

const categoryColors = {
  feature: "bg-purple-100 text-purple-700 border-purple-200",
  improvement: "bg-blue-100 text-blue-700 border-blue-200",
  bug: "bg-red-100 text-red-700 border-red-200"
};

const statusConfig = {
  pending: { icon: <Clock className="w-3.5 h-3.5" />, color: "bg-amber-100 text-amber-700", label: "Pending" },
  planned: { icon: <TrendingUp className="w-3.5 h-3.5" />, color: "bg-blue-100 text-blue-700", label: "Planned" },
  in_progress: { icon: <Zap className="w-3.5 h-3.5" />, color: "bg-indigo-100 text-indigo-700", label: "In Progress" },
  completed: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, color: "bg-green-100 text-green-700", label: "Completed" },
  declined: { icon: <XCircle className="w-3.5 h-3.5" />, color: "bg-gray-100 text-gray-700", label: "Declined" }
};

const AdminStatusSelect = ({ feature, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await onStatusChange(feature.id, newStatus);
      setIsOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Select 
      value={feature.status} 
      onValueChange={handleStatusChange}
      disabled={updating}
    >
      <SelectTrigger 
        className="w-32 h-8 text-xs rounded-lg border-charcoal/20"
        data-testid={`admin-status-${feature.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="pending">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-amber-600" />
            Pending
          </div>
        </SelectItem>
        <SelectItem value="planned">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-blue-600" />
            Planned
          </div>
        </SelectItem>
        <SelectItem value="in_progress">
          <div className="flex items-center gap-2">
            <Zap className="w-3 h-3 text-indigo-600" />
            In Progress
          </div>
        </SelectItem>
        <SelectItem value="completed">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-3 h-3 text-green-600" />
            Completed
          </div>
        </SelectItem>
        <SelectItem value="declined">
          <div className="flex items-center gap-2">
            <XCircle className="w-3 h-3 text-gray-600" />
            Declined
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

const FeatureCard = ({ feature, onVote, onStatusChange, onDelete, userId, isAdmin }) => {
  const netVotes = feature.upvotes - feature.downvotes;
  const userVote = feature.user_vote;
  
  return (
    <div 
      className="bg-card dark:bg-card rounded-2xl p-4 border border-charcoal/5 dark:border-white/10 hover:shadow-lg transition-all"
      data-testid={`feature-${feature.id}`}
    >
      {/* Header row with status/admin controls */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-charcoal dark:text-white text-base leading-tight flex-1">{feature.title}</h3>
        <div className="flex items-center gap-1.5 shrink-0">
          {isAdmin ? (
            <>
              <AdminStatusSelect feature={feature} onStatusChange={onStatusChange} />
              <button
                onClick={() => onDelete(feature.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500 transition-colors"
                title="Delete"
                data-testid={`delete-${feature.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig[feature.status]?.color}`}>
              {statusConfig[feature.status]?.icon}
              <span>{statusConfig[feature.status]?.label}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Description */}
      <p className="text-muted-foreground text-sm mb-3 line-clamp-2 leading-relaxed">
        {feature.description}
      </p>
      
      {/* Footer row: Vote buttons on left, meta on right */}
      <div className="flex items-center justify-between">
        {/* Vote buttons - horizontal layout */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onVote(feature.id, userVote === 1 ? 0 : 1)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
              userVote === 1 
                ? "bg-primary text-white shadow-md" 
                : "bg-muted dark:bg-white/10 hover:bg-primary/10 text-muted-foreground hover:text-primary"
            }`}
            data-testid={`upvote-${feature.id}`}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            <span className="font-medium">{feature.upvotes}</span>
          </button>
          
          <button
            onClick={() => onVote(feature.id, userVote === -1 ? 0 : -1)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
              userVote === -1 
                ? "bg-red-500 text-white shadow-md" 
                : "bg-muted dark:bg-white/10 hover:bg-red-50 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500"
            }`}
            data-testid={`downvote-${feature.id}`}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
            <span className="font-medium">{feature.downvotes}</span>
          </button>
        </div>
        
        {/* Meta info */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${categoryColors[feature.category]}`}>
            {categoryIcons[feature.category]}
            <span className="capitalize hidden sm:inline">{feature.category}</span>
          </span>
          <span className="hidden sm:inline text-muted-foreground/70">{feature.created_by_name}</span>
        </div>
      </div>
    </div>
  );
};

const FeedbackPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("votes");
  const [submitting, setSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "feature"
  });

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API}/admin/check`);
        setIsAdmin(res.data.is_admin);
        if (res.data.is_admin) {
          // Fetch admin stats
          const statsRes = await axios.get(`${API}/admin/feedback/stats`);
          setAdminStats(statsRes.data);
        }
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  const fetchFeatures = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      params.append("sort_by", sortBy);
      
      const res = await axios.get(`${API}/feedback?${params.toString()}`);
      setFeatures(res.data);
    } catch (error) {
      console.error("Failed to fetch features:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [filter, sortBy]);

  const handleVote = async (featureId, vote) => {
    try {
      await axios.post(`${API}/feedback/${featureId}/vote`, { vote });
      
      // Optimistic update
      setFeatures(prev => prev.map(f => {
        if (f.id !== featureId) return f;
        
        const oldVote = f.user_vote || 0;
        let newUpvotes = f.upvotes;
        let newDownvotes = f.downvotes;
        
        // Remove old vote effect
        if (oldVote === 1) newUpvotes--;
        if (oldVote === -1) newDownvotes--;
        
        // Add new vote effect
        if (vote === 1) newUpvotes++;
        if (vote === -1) newDownvotes++;
        
        return {
          ...f,
          upvotes: newUpvotes,
          downvotes: newDownvotes,
          user_vote: vote === 0 ? null : vote
        };
      }));
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleStatusChange = async (featureId, newStatus) => {
    try {
      await axios.put(`${API}/admin/feedback/${featureId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      
      // Update local state
      setFeatures(prev => prev.map(f => 
        f.id === featureId ? { ...f, status: newStatus } : f
      ));
      
      // Refresh admin stats
      const statsRes = await axios.get(`${API}/admin/feedback/stats`);
      setAdminStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (featureId) => {
    if (!confirm("Are you sure you want to delete this feature request?")) return;
    
    try {
      await axios.delete(`${API}/feedback/${featureId}`);
      toast.success("Feature request deleted");
      setFeatures(prev => prev.filter(f => f.id !== featureId));
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, formData);
      toast.success("Feature request submitted!");
      setShowAddModal(false);
      setFormData({ title: "", description: "", category: "feature" });
      fetchFeatures();
    } catch (error) {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    total: features.length,
    pending: features.filter(f => f.status === "pending").length,
    planned: features.filter(f => f.status === "planned").length,
    completed: features.filter(f => f.status === "completed").length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-charcoal/5 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-xl sm:text-2xl text-charcoal">Feedback Board</h1>
                  {isAdmin && (
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Vote on features & share your ideas</p>
              </div>
            </div>
            
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-xl gap-2 text-sm sm:text-base px-3 sm:px-4">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Request</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-card border-none rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl flex items-center gap-2">
                    <MessageSquarePlus className="w-5 h-5 text-primary" />
                    Submit Feature Request
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1.5 block">Title</label>
                    <Input
                      placeholder="Short, descriptive title..."
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="rounded-xl border-charcoal/10"
                      data-testid="feedback-title-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1.5 block">Category</label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="rounded-xl border-charcoal/10" data-testid="feedback-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="feature">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-purple-500" />
                            Feature Request
                          </div>
                        </SelectItem>
                        <SelectItem value="improvement">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-blue-500" />
                            Improvement
                          </div>
                        </SelectItem>
                        <SelectItem value="bug">
                          <div className="flex items-center gap-2">
                            <Bug className="w-4 h-4 text-red-500" />
                            Bug Report
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1.5 block">Description</label>
                    <Textarea
                      placeholder="Describe your idea in detail..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-xl border-charcoal/10 min-h-[120px]"
                      data-testid="feedback-description-input"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="bg-primary text-white hover:bg-primary/90 rounded-xl"
                      data-testid="submit-feedback-btn"
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Stats Bar - Single unified section for both admin and regular users */}
      <div className="bg-card border-b border-charcoal/5">
        <div className="max-w-5xl mx-auto px-4 py-4">
          {/* Admin extra info - compact row */}
          {isAdmin && adminStats && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 pb-3 border-b border-charcoal/5">
              <span className="font-medium">{adminStats.total_features} requests</span>
              <span>•</span>
              <span>{adminStats.total_votes} total votes</span>
            </div>
          )}
          
          {/* Stats icons - 4 column grid */}
          <div className="grid grid-cols-4 gap-1 text-center">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-1">
                <MessageSquarePlus className="w-5 h-5 text-primary" />
              </div>
              <div className="font-semibold text-charcoal dark:text-white text-base">{stats.total}</div>
              <div className="text-[10px] text-muted-foreground">Total</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-1">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="font-semibold text-charcoal dark:text-white text-base">{stats.pending}</div>
              <div className="text-[10px] text-muted-foreground">Pending</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-1">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="font-semibold text-charcoal dark:text-white text-base">{stats.planned}</div>
              <div className="text-[10px] text-muted-foreground">Planned</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div className="font-semibold text-charcoal dark:text-white text-base">{stats.completed}</div>
              <div className="text-[10px] text-muted-foreground">Done</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - improved mobile layout */}
      <div className="max-w-5xl mx-auto px-4 py-3">
        <div className="flex flex-col gap-3">
          {/* Tabs - full width, scrollable */}
          <div className="overflow-x-auto -mx-4 px-4 pb-1">
            <Tabs value={filter} onValueChange={setFilter} className="w-max">
              <TabsList className="bg-muted/50 dark:bg-white/10 rounded-xl h-9 p-1">
                <TabsTrigger value="all" className="rounded-lg text-xs px-3 h-7">All</TabsTrigger>
                <TabsTrigger value="pending" className="rounded-lg text-xs px-3 h-7">Pending</TabsTrigger>
                <TabsTrigger value="planned" className="rounded-lg text-xs px-3 h-7">Planned</TabsTrigger>
                <TabsTrigger value="in_progress" className="rounded-lg text-xs px-3 h-7">In Progress</TabsTrigger>
                <TabsTrigger value="completed" className="rounded-lg text-xs px-3 h-7">Done</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Sort dropdown */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full rounded-xl border-charcoal/10 dark:border-white/20 h-9">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="votes">Most Votes</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Feature List */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : features.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-muted dark:bg-white/10 flex items-center justify-center mx-auto mb-4">
              <Lightbulb className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl text-charcoal dark:text-white mb-2">No feature requests yet</h3>
            <p className="text-muted-foreground mb-6">Be the first to share your ideas!</p>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-primary text-white hover:bg-primary/90 rounded-xl gap-2"
            >
              <Plus className="w-4 h-4" />
              Submit First Request
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {features.map((feature) => (
              <FeatureCard 
                key={feature.id} 
                feature={feature} 
                onVote={handleVote}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                userId={user?.id}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;
